// packages/studio/web/src/proposals/ChangeDocButton.tsx
//
// Authoring affordance for a proposal's optional change document (Phase B8). A
// change document is a real `.md` file in the workspace VFS under the
// proposal's target (<target-dir>/docs/decisions/<date>-<slug>.md). "Add change
// document" creates it as an editor_draft (frontmatter + boilerplate), records
// the pointer (docPath), and opens it in the shared markdown editor; once set,
// the button reopens it and a "documented" badge shows. It materialises to git
// on propose via the normal editor_drafts → commit path, so the decision travels
// with the diff that enacts it.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { WorkspaceStore } from "@archlang/app-shell/workbench";
import { Tooltip } from "@archlang/ui";
import { Button } from "../ui/Button";
import { useToast } from "../ui/Toast";
import { cacheChangeset } from "./changesetCache";
import {
  discoverContainers,
  patchChangeset,
  type Changeset,
} from "./proposalsApi";
import {
  composeChangeDoc,
  decisionDocPath,
  encodeTarget,
  frontmatterSpace,
  parseTarget,
  primaryPackageTarget,
  resolveTargetDir,
  slugifyTitle,
} from "./changeDoc";
import { useTranslation } from "../i18n";

export interface ChangeDocButtonProps {
  readonly slug: string;
  readonly repo: string;
  readonly changeset: Changeset;
  readonly store: WorkspaceStore;
  /** Draft paths touched by this changeset — drives the default target. */
  readonly touchedPaths: readonly string[];
  /** Trunk ref the container discovery walks. */
  readonly baseRef: string;
  /** Current change title (inline name), used for the slug + frontmatter. */
  readonly title: string;
}

function today(): string {
  return new Date().toISOString().slice(0, 10);
}

function shortId(): string {
  const uuid = globalThis.crypto?.randomUUID?.();
  return uuid ? uuid.replace(/-/g, "").slice(0, 8) : `${Date.now().toString(36)}`;
}

export function ChangeDocButton({
  slug,
  repo,
  changeset,
  store,
  touchedPaths,
  baseRef,
  title,
}: ChangeDocButtonProps) {
  const { t } = useTranslation("proposals");
  const toast = useToast();
  const queryClient = useQueryClient();

  const containers = useQuery({
    queryKey: ["containers", slug, repo, baseRef],
    queryFn: () => discoverContainers(slug, repo, baseRef),
  });

  const openDoc = (path: string) => store.openTab({ kind: "doc", path });

  const create = useMutation({
    mutationFn: async () => {
      const discovery = containers.data ?? (await discoverContainers(slug, repo, baseRef));
      // Target: the proposal's if set, else the primary package (owner of the
      // touched files). Persisted alongside docPath so scope + doc home agree.
      const targetStr = changeset.target || primaryPackageTarget(touchedPaths, discovery.packages);
      const target = parseTarget(targetStr);
      const dir = resolveTargetDir(target, discovery);
      // Seeds the doc slug + frontmatter title (git-committed content), so this
      // fallback stays a stable literal — not localised UI chrome.
      const docTitle = (title || changeset.title || "Change").trim() || "Change";
      const path = decisionDocPath(dir, today(), slugifyTitle(docTitle));
      const content = composeChangeDoc({
        changeId: shortId(),
        title: docTitle,
        date: today(),
        space: frontmatterSpace(target, discovery),
        proposal: changeset.number ? `#${changeset.number}` : undefined,
      });
      // Record the pointer + resolved target FIRST (the fallible step). Only
      // once it lands do we create the draft file (a sync store edit that can't
      // reject) — so a failed PATCH never orphans a doc the proposal can't see.
      const updated = await patchChangeset(slug, repo, changeset.id, {
        docPath: path,
        target: encodeTarget(target.kind, target.path),
      });
      store.edit(path, content);
      return { updated, path };
    },
    onSuccess: ({ updated, path }) => {
      cacheChangeset(queryClient, slug, repo, updated);
      openDoc(path);
      toast.success(t("changeDoc.toastAdded"));
    },
    onError: (err: Error) => toast.error(err.message),
  });

  if (changeset.docPath) {
    return (
      <Tooltip content={t("changeDoc.open", { path: changeset.docPath })}>
        <button
          type="button"
          onClick={() => openDoc(changeset.docPath!)}
          className="inline-flex items-center gap-1.5 rounded-full bg-arch-500/15 px-2.5 py-1 text-xs font-medium text-arch-200 ring-1 ring-inset ring-arch-500/30 hover:bg-arch-500/25"
        >
          <span aria-hidden="true">🖹</span>
          {t("changeDoc.documented")}
        </button>
      </Tooltip>
    );
  }

  return (
    <Tooltip content={t("changeDoc.addTooltip")}>
      <Button
        variant="ghost"
        size="sm"
        disabled={create.isPending}
        onClick={() => create.mutate()}
      >
        {create.isPending ? t("changeDoc.adding") : t("changeDoc.add")}
      </Button>
    </Tooltip>
  );
}
